import styled from '@emotion/styled'
import React, { useMemo, useState } from 'react'
import { format } from 'date-fns'
import { Briefcase } from '@emotion-icons/fa-solid/Briefcase'
import { OpenInNew } from '@emotion-icons/material-rounded/OpenInNew'
import { ArrowUp } from '@emotion-icons/fluentui-system-filled/ArrowUp'
import { ArrowDown } from '@emotion-icons/fluentui-system-filled/ArrowDown'

import { MapSquareIcon } from 'src/components/icons/map-square'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { Colors } from 'src/components/theme'
import { MapTooltip } from 'src/v2/components/map-tooltip'
import { FlexColumn, FlexRow } from 'src/components/flex-containers'
import { formatVal } from 'src/utils/number-format'
import { SidebarResultOverlayList, SidebarResultOverlayListItem } from 'src/v2/components/sidebar-result-overlay-list'
import { TransactionType } from 'src/v2/views/owner-reveal/insights/owner-summary/activity-heatmap/types'
import { AMParcelOverview } from 'src/common-services'
import { getEntityLink } from 'src/v2/utils/entity'
import { lookupParcelInMapExplorer } from 'src/v2/utils/parcel'

const IS_FINAL_VERSION = false // TODO: remove when implemented

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 32px;
  background-color: ${Colors.primaryGreen700};
`

const TransactionTypeContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  font-weight: 700;
  font-size: 16px;
`

const PurchaseIndicator = styled(TransactionTypeContainer)`
  background-color: ${Colors.primaryGreen100};
  color: ${Colors.primaryGreen600};
`

const SaleIndicator = styled(TransactionTypeContainer)`
  background-color: ${Colors.primaryRed100};
  color: ${Colors.primaryRed600};
`

const Title = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: ${Colors.neutral950};
`

const APN = styled.div`
  font-weight: 500;
  font-size: 12px;
  color: ${Colors.neutral500};
`

const OpenButtonContainer = styled.div`
  margin: 0 -8px;
`

type PropertyTooltipHeadingProps = {
  address: string
  apn: string
  onClickOpenInMapExplorer: () => void
}

const PropertyTooltipHeading: React.FC<PropertyTooltipHeadingProps> = ({ address, apn, onClickOpenInMapExplorer }) => {
  return (
    <HeadingContainer>
      <FlexRow gap={8} align="center">
        <IconContainer>
          <MapSquareIcon size={14} color={Colors.bgWhite} />
        </IconContainer>
        <FlexColumn>
          <Title>{address}</Title>
          <APN>APN # {apn}</APN>
        </FlexColumn>
      </FlexRow>
      <FlexRow>
        <OpenButtonContainer>
          <ButtonWithIcon ghost onClick={onClickOpenInMapExplorer}>
            <OpenInNew size={16} /> Open in Map Explorer
          </ButtonWithIcon>
        </OpenButtonContainer>
      </FlexRow>
    </HeadingContainer>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 380px;
`

const SectionContainer = styled.div`
  flex: 1;
`

const SectionTitle = styled.div`
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
  color: ${Colors.neutral400};
`

const SectionValue = styled.div`
  display: flex;
  gap: 12px;
  font-weight: 700;
  font-size: 16px;
  color: ${Colors.neutral700};
`

type PropertyTooltipProps = {
  onMouseEnter: () => void
  onMouseLeave: () => void
  onClickItem: (item: SidebarResultOverlayListItem) => void
  // TODO: attributes blow should come from the parcel overview endpoint and be part of AMParcelOverview type
  transactionType: TransactionType
  price: number
  date: string
  parcel: AMParcelOverview
}

export const PropertyTooltip: React.FC<PropertyTooltipProps> = ({
  parcel,
  transactionType,
  price,
  date,
  onMouseEnter,
  onMouseLeave,
  onClickItem,
}) => {
  const [showAllOwners, setShowAllOwners] = useState<boolean>(false)

  const IndicatorIcon = useMemo(() => {
    switch (transactionType) {
      case TransactionType.Purchase:
        return ArrowUp
      case TransactionType.Sale:
        return ArrowDown
    }
  }, [transactionType])

  const indicatorLabel = useMemo(() => {
    switch (transactionType) {
      case TransactionType.Purchase:
        return 'Purchase'
      case TransactionType.Sale:
        return 'Sale'
      default:
        return null
    }
  }, [transactionType])

  const IndicatorComponent = useMemo(() => {
    if (!IS_FINAL_VERSION) {
      return null
    }

    switch (transactionType) {
      case TransactionType.Purchase:
        return PurchaseIndicator
      case TransactionType.Sale:
        return SaleIndicator
      default:
        return null
    }
  }, [transactionType])

  const transactionIndicator = useMemo(() => {
    if (IndicatorComponent !== null) {
      return (
        <IndicatorComponent>
          <IndicatorIcon size={16} /> {indicatorLabel}
        </IndicatorComponent>
      )
    }
    return null
  }, [transactionType])

  const reportedOwners = useMemo(
    () =>
      parcel.owners.map((owner) => ({
        id: owner.id,
        icon: <Briefcase size={10} />,
        iconBgColor: Colors.primaryRed800,
        link: getEntityLink(owner.id),
        linkIcon: <OpenInNew size={18} />,
        title: owner.name,
        subtitle: owner.address,
      })) ?? [],
    [parcel.owners]
  )

  const handleShowAllOwners = () => setShowAllOwners(true)

  const onOpenParcel = () => {
    lookupParcelInMapExplorer(parcel)
  }

  return (
    <MapTooltip
      tab={transactionIndicator}
      heading={
        <PropertyTooltipHeading address={parcel.address} apn={parcel.apn} onClickOpenInMapExplorer={onOpenParcel} />
      }
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ maxHeight: 400, overflowY: 'auto' }}>
      <Container>
        <FlexColumn gap={20}>
          {IS_FINAL_VERSION && (
            <FlexRow gap={16}>
              <SectionContainer>
                <SectionTitle>Purchase Price</SectionTitle>
                <SectionValue>{formatVal(price, 'currency')}</SectionValue>
              </SectionContainer>
              <SectionContainer>
                <SectionTitle>Date</SectionTitle>
                <SectionValue>{format(new Date(date), 'MM/dd/yyyy')}</SectionValue>
              </SectionContainer>
            </FlexRow>
          )}
          <FlexRow>
            <SidebarResultOverlayList
              title="Reported Owners"
              items={reportedOwners}
              showAll={showAllOwners}
              onClickViewMore={handleShowAllOwners}
              onClickItem={onClickItem}
            />
          </FlexRow>
        </FlexColumn>
      </Container>
    </MapTooltip>
  )
}
